import type { PontoDoAnalitoResponse, SerieDoAnalitoResponse } from '@/api/generated/model';
import { rotuloDeExterno } from '@/features/historico/pontoExterno';
import { Badge } from '@/shared/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const INTERPRETACOES: Record<string, { rotulo: string; classe: string }> = {
  NORMAL: { rotulo: 'Normal', classe: 'text-emerald-600 border-emerald-600/40' },
  ABAIXO: { rotulo: 'Abaixo', classe: 'text-sky-600 border-sky-600/40' },
  ACIMA: { rotulo: 'Acima', classe: 'text-amber-600 border-amber-600/40' },
  INDETERMINADO: { rotulo: 'Indeterminado', classe: 'text-muted-foreground border-border' },
};

interface Props {
  serie: SerieDoAnalitoResponse;
  /** Quantas medições mostrar, da mais recente. A V1 fixava 5. */
  limite?: number;
}

/**
 * Comparação das últimas medições (F6-1).
 *
 * ⚠️ **A faixa é a de HOJE, não a da emissão.** `serie.faixaTexto` vem do
 * cadastro atual para a espécie, sexo e idade do paciente, então é a mesma em
 * todas as linhas — a interpretação de cada ponto, essa sim, é a que foi
 * gravada quando o resultado saiu. Um analito recadastrado no meio do caminho
 * pode, por isso, mostrar "Acima" ao lado de uma faixa que hoje o comportaria;
 * é o histórico dizendo a verdade sobre o que foi laudado.
 */
export function TabelaComparativa({ serie, limite = 5 }: Props) {
  const linhas = [...serie.pontos].reverse().slice(0, limite);

  if (linhas.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Resultado</TableHead>
            <TableHead>Faixa de referência</TableHead>
            <TableHead>Interpretação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {linhas.map((ponto) => (
            <TableRow key={ponto.exameId}>
              <TableCell className="text-muted-foreground whitespace-nowrap">
                {new Date(ponto.medidoEm).toLocaleDateString('pt-BR')}
                {rotuloDeExterno(ponto) && (
                  // Na tabela o laboratório cabe por extenso — é onde a pessoa
                  // confere de onde veio o número que destoa.
                  <span className="block text-xs">{rotuloDeExterno(ponto)}</span>
                )}
              </TableCell>

              <TableCell className="font-medium">
                {valorDoPonto(ponto)}
                {ponto.valor !== null && ponto.unidade && (
                  <span className="text-muted-foreground font-normal ml-1">{ponto.unidade}</span>
                )}
              </TableCell>

              <TableCell className="text-muted-foreground whitespace-nowrap">
                {serie.faixaTexto ?? (
                  <span className="italic">Não cadastrada para esta espécie</span>
                )}
              </TableCell>

              <TableCell>
                {ponto.interpretacao ? (
                  <Badge
                    variant="outline"
                    className={INTERPRETACOES[ponto.interpretacao]?.classe ?? ''}
                  >
                    {INTERPRETACOES[ponto.interpretacao]?.rotulo ?? ponto.interpretacao}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function valorDoPonto(ponto: PontoDoAnalitoResponse): string {
  if (ponto.valor !== null)
    return ponto.valor.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  return ponto.valorTexto ?? '—';
}
